/**
 * @fileoverview Animated navigation link component with smooth hover transitions.
 * Features sliding text animation and customizable styling for navigation menus.
 */

import { useState } from 'react';
import { motion, Transition } from 'motion/react';
import type { NavLinkProps } from '../types/components';

/**
 * Animated navigation link with sliding text hover effect.
 *
 * @description Creates a link with smooth sliding text animation on hover.
 * The original text slides up while a duplicate slides up from below,
 * creating a seamless transition effect.
 *
 * @param {NavLinkProps} props - Component properties
 * @param {string} props.href - Link destination URL
 * @param {string} props.text - Text content to display
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {string} [props.target] - Link target attribute (_self, _blank, etc.)
 * @param {() => void} [props.onClick] - Click event handler
 *
 * @returns {JSX.Element} Animated navigation link component
 *
 * @example
 * ```tsx
 * // Basic navigation link
 * <NavLink href="/about" text="About" />
 *
 * // External link with custom styling
 * <NavLink
 *   href="https://example.com"
 *   text="External Site"
 *   target="_blank"
 *   className="text-blue-500 hover:text-blue-700"
 * />
 *
 * // Link with click handler (useful for smooth scrolling)
 * <NavLink
 *   href="#section"
 *   text="Section"
 *   onClick={() => scrollToSection('section')}
 * />
 * ```
 */
export default function NavLink({
  href,
  text,
  className = '',
  target,
  onClick,
}: NavLinkProps) {
  /** Controls hover animation state */
  const [isHovered, setIsHovered] = useState(false);

  /** Animation variants for main text (slides up on hover) */
  const textVariants = {
    initial: { y: '0%', opacity: 1 },
    hover: { y: '-100%', opacity: 0 },
  };

  /** Animation variants for duplicate text (slides up from below) */
  const textBottomVariants = {
    initial: { y: '100%', opacity: 0 },
    hover: { y: '0%', opacity: 1 },
  };

  /** Fast, linear transition for smooth text sliding */
  const transition: Transition = {
    duration: 0.12,
    ease: 'linear',
  };

  return (
    <motion.a
      href={href}
      className={`relative overflow-hidden inline-block ${className}`}
      target={target ? target : '_self'}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main text element - slides up on hover */}
      <motion.span
        className="inline-block"
        variants={textVariants}
        initial="initial"
        animate={isHovered ? 'hover' : 'initial'}
        transition={transition}
      >
        {text}
      </motion.span>

      {/* Duplicate text element - slides up from below on hover */}
      <motion.span
        className="absolute left-0 top-0 inline-block"
        variants={textBottomVariants}
        initial="initial"
        animate={isHovered ? 'hover' : 'initial'}
        transition={transition}
      >
        {text}
      </motion.span>
    </motion.a>
  );
}
